// =============================================================================
// Email Express — preset-template section renderers (Task #975)
// -----------------------------------------------------------------------------
// Deterministic renderers for the 18 preset section primitives defined in
// shared/email-presets.ts. A preset email's layout is FIXED by its template;
// only colors/fonts resolve from the brand (ctx.style, resolveEmailBrandStyle),
// so every renderer here is brand-neutral and reads brand tokens exclusively
// from ctx.style — never literal hexes from the reference designs.
//
// Copy lives at blockCopy.instances[<sectionId>].preset (EmailPresetSectionCopy)
// and is click-to-edit in the editor via the same patch-instance-copy plumbing
// archetype sections use. Emphasis convention: **bold** and _italic_ markers in
// the stored copy are parsed at render time (raw markers shown only while a
// field is actively being edited).
//
// AI image slots (ctx.presetImageUrls/-Status keyed by section id) show a
// shimmer while pending and swap to the persisted R2 image when ready — the
// capture path replaces each URL with a same-origin data URL (presetExportUrls)
// exactly like the archetype supporting images.
// =============================================================================
import React, { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { PreviewCtx } from "@/lib/email-render";
import {
  getEmailPresetTemplate,
  EMAIL_PRESET_DEFAULT_BADGES,
  type EmailPresetSectionCopy,
  type EmailPresetInputs,
} from "@shared/email-presets";
import { safeFooterHref, type UniversalSocialLink } from "@shared/schema";
import {
  BadgeCheck,
  Check,
  HeartPulse,
  Activity,
  Gauge,
  ShieldCheck,
  Star,
  Truck,
  X,
} from "lucide-react";

// ── Copy access -──────────────────────────────────────────────────────────────
function presetCopyOf(ctx: PreviewCtx, key: string): EmailPresetSectionCopy {
  const inst = (ctx.blockCopy?.instances as any)?.[key];
  const p = inst?.preset;
  return p && typeof p === "object" ? (p as EmailPresetSectionCopy) : {};
}

function patchPreset(
  ctx: PreviewCtx,
  key: string,
  patch: Partial<EmailPresetSectionCopy>,
) {
  if (!ctx.editor) return;
  const cur = presetCopyOf(ctx, key);
  ctx.editor.onPatchInstanceCopy(key, {
    preset: { ...cur, ...patch },
  } as any);
}

function presetInputs(ctx: PreviewCtx): EmailPresetInputs {
  return (ctx.presetData ?? {}) as EmailPresetInputs;
}

// ── Emphasis parsing (**bold** / _italic_) ────────────────────────────────────
// Splits on **…** first, then _…_ inside plain runs. Returns render nodes; the
// caller styles bold/italic via the supplied inline styles so a dark band can
// e.g. render bold phrases in the accent color.
export function parseEmphasis(
  text: string,
  opts?: { bold?: CSSProperties; italic?: CSSProperties },
): ReactNode {
  const out: ReactNode[] = [];
  let k = 0;
  const pushItalics = (chunk: string) => {
    const parts = chunk.split(/_([^_]+)_/g);
    parts.forEach((p, i) => {
      if (!p) return;
      if (i % 2 === 1) {
        out.push(
          <em key={`i${k++}`} style={opts?.italic}>
            {p}
          </em>,
        );
      } else {
        out.push(<React.Fragment key={`t${k++}`}>{p}</React.Fragment>);
      }
    });
  };
  const boldParts = String(text ?? "").split(/\*\*([^*]+)\*\*/g);
  boldParts.forEach((p, i) => {
    if (!p) return;
    if (i % 2 === 1) {
      out.push(
        <strong key={`b${k++}`} style={opts?.bold}>
          {p}
        </strong>,
      );
    } else {
      pushItalics(p);
    }
  });
  return out;
}

// ── Click-to-edit text that renders PARSED emphasis in view mode ─────────────
// Mirrors EmailEditableText's editing behavior (contentEditable, Enter commits,
// Esc cancels) but the resting state shows the parsed **bold**/_italic_ nodes;
// raw markers only appear while the field is focused for editing. Send/export
// paths (no ctx.editor) render the parsed static node only.
function PText({
  ctx,
  blockKey,
  field,
  value,
  placeholder,
  as = "div",
  className,
  style,
  bold,
  italic,
  testId,
  multiline = false,
  onCommit,
}: {
  ctx: PreviewCtx;
  blockKey: string;
  field?: keyof EmailPresetSectionCopy;
  value: string | null | undefined;
  placeholder?: string;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  style?: CSSProperties;
  bold?: CSSProperties;
  italic?: CSSProperties;
  testId?: string;
  multiline?: boolean;
  /** Custom commit (array items etc.). Defaults to patching `field`. */
  onCommit?: (v: string) => void;
}) {
  const ref = useRef<HTMLElement>(null);
  const [editing, setEditing] = useState(false);
  const cancelRef = useRef(false);
  const seededRef = useRef("");

  useEffect(() => {
    if (!editing || !ref.current) return;
    const el = ref.current;
    const seed = (value ?? "") !== "" ? (value as string) : (placeholder ?? "");
    seededRef.current = seed;
    el.textContent = seed;
    el.focus();
    const doc = el.ownerDocument;
    const range = doc.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = doc.defaultView?.getSelection() ?? null;
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const commit = () => {
    const el = ref.current;
    setEditing(false);
    if (!el) return;
    const next = (el.textContent ?? "").trim();
    if (next === seededRef.current.trim()) return;
    if (next === (value ?? "").trim()) return;
    if (onCommit) onCommit(next);
    else if (field) patchPreset(ctx, blockKey, { [field]: next } as any);
  };

  const display = (value ?? "").trim() || placeholder || "";
  const body = parseEmphasis(display, { bold, italic });

  if (ctx.editor && editing) {
    return React.createElement(as, {
      ref,
      className: `${className ?? ""} outline outline-2 outline-dashed outline-primary rounded-sm`,
      style: { ...style, whiteSpace: multiline ? "pre-wrap" : undefined, cursor: "text" },
      contentEditable: true,
      suppressContentEditableWarning: true,
      "data-testid": testId,
      onBlur: () => {
        if (cancelRef.current) {
          cancelRef.current = false;
          setEditing(false);
        } else {
          commit();
        }
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !(multiline && e.shiftKey)) {
          e.preventDefault();
          (ref.current as HTMLElement | null)?.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancelRef.current = true;
          (ref.current as HTMLElement | null)?.blur();
        }
      },
    });
  }
  if (ctx.editor) {
    return React.createElement(
      as,
      {
        className: `${className ?? ""} cursor-text rounded-sm hover:outline hover:outline-1 hover:outline-dashed hover:outline-primary/60`,
        style,
        "data-testid": testId,
        title: "Click to edit",
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          setEditing(true);
        },
      },
      body,
    );
  }
  return React.createElement(as, { className, style, "data-testid": testId }, body);
}

// ── Shared visual primitives ─────────────────────────────────────────────────

// CTA button styled from the brand button tokens; label editable via `field`
// (default "ctaLabel") on the section's preset copy.
function PresetCta({
  ctx,
  blockKey,
  label,
  fallback = "Shop now",
  field = "ctaLabel",
  outlineOnDark = false,
  className,
}: {
  ctx: PreviewCtx;
  blockKey: string;
  label?: string | null;
  fallback?: string;
  field?: keyof EmailPresetSectionCopy;
  /** Render as a light outline button (for dark bands). */
  outlineOnDark?: boolean;
  className?: string;
}) {
  const { button, fonts } = ctx.style;
  const style: CSSProperties = outlineOnDark
    ? {
        backgroundColor: "transparent",
        color: "#FFFFFF",
        border: "2px solid rgba(255,255,255,0.85)",
        borderRadius: button.borderRadius,
        fontFamily: fonts.body,
      }
    : {
        backgroundColor: button.backgroundColor,
        color: button.color,
        borderRadius: button.borderRadius,
        // Brand CTA style extras (outline border + CTA font); undefined ⇒
        // byte-identical legacy render. The dark-band outline variant above
        // deliberately keeps its fixed white treatment.
        border: button.border ?? undefined,
        fontFamily: button.fontFamily ?? fonts.body,
      };
  return (
    <div className={className ?? "mt-5"}>
      <div
        className="inline-block px-8 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em] text-center"
        style={style}
        data-testid={`preset-cta-${blockKey}`}
      >
        <PText
          ctx={ctx}
          blockKey={blockKey}
          field={field}
          value={label}
          placeholder={fallback}
          as="span"
          testId={`editable-preset-cta-${blockKey}`}
        />
      </div>
    </div>
  );
}

// Resolve a raw image URL through the capture export map (same-origin data URL
// during PNG capture, the raw URL otherwise).
function exportSafe(ctx: PreviewCtx, url?: string | null): string | null {
  if (!url) return null;
  return ctx.presetUrlExportUrls?.[url] ?? url;
}

// The email's main product photo (real photo, never AI).
function productPhotoUrl(ctx: PreviewCtx): string | null {
  const raw = ctx.productImageUrls?.[0] ?? null;
  if (!raw) return null;
  return ctx.productExportUrls?.[0] ?? raw;
}

// An AI image slot keyed by section id: shimmer while pending, image when
// ready (export data URL during capture), null when failed/absent — callers
// degrade to a no-image layout.
function PresetAiImage({
  ctx,
  slotId,
  className,
  imgClassName,
  alt,
}: {
  ctx: PreviewCtx;
  slotId: string;
  className?: string;
  imgClassName?: string;
  alt: string;
}) {
  const status = ctx.presetImageStatus?.[slotId] ?? null;
  const url = ctx.presetExportUrls?.[slotId] ?? ctx.presetImageUrls?.[slotId] ?? null;
  if (status === "ready" && url) {
    return (
      <div className={className}>
        <img
          src={url}
          alt={alt}
          className={imgClassName ?? "w-full h-auto object-cover"}
          data-testid={`img-preset-${slotId}`}
        />
      </div>
    );
  }
  if (status === "pending") {
    return (
      <div
        data-export-exclude
        className={`${className ?? ""} email-skeleton-shimmer`}
        style={
          {
            minHeight: "180px",
            "--shimmer-base": "#e2e8f0",
            "--shimmer-glow": "#f1f5f9",
          } as CSSProperties
        }
        data-testid={`skeleton-preset-${slotId}`}
      />
    );
  }
  return null;
}

// Edit an item inside a string array field.
function patchArrayItem(
  ctx: PreviewCtx,
  blockKey: string,
  field: keyof EmailPresetSectionCopy,
  index: number,
  value: string,
) {
  const cur = presetCopyOf(ctx, blockKey);
  const arr = Array.isArray((cur as any)[field])
    ? [...((cur as any)[field] as string[])]
    : [];
  while (arr.length <= index) arr.push("");
  arr[index] = value;
  patchPreset(ctx, blockKey, { [field]: arr } as any);
}

const TRUST_ICONS = [ShieldCheck, BadgeCheck, Truck, Check];
const STAT_ICONS = [HeartPulse, Activity, Gauge];

function Stars({ rating, color }: { rating: number; color: string }) {
  const n = Math.max(1, Math.min(5, Math.round(rating || 5)));
  return (
    <div className="flex items-center justify-center gap-0.5">
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} className="h-4 w-4" style={{ color }} fill={color} />
      ))}
    </div>
  );
}

// ── Section renderers ─────────────────────────────────────────────────────────

function HeaderSection({ ctx }: { ctx: PreviewCtx }) {
  const { colors, fonts } = ctx.style;
  const logo = ctx.logoExportUrl || ctx.style.logoUrl;
  return (
    <div
      className="text-center px-6 py-5"
      style={{ backgroundColor: colors.card }}
      data-testid="preset-section-header-logo"
    >
      {logo ? (
        <img
          src={logo}
          alt={ctx.brandName}
          className="mx-auto h-10 w-auto object-contain"
        />
      ) : (
        <div
          className="text-sm font-semibold uppercase tracking-[0.22em]"
          style={{ color: colors.text, fontFamily: fonts.heading }}
        >
          {ctx.brandName}
        </div>
      )}
    </div>
  );
}

function HeroSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const copy = presetCopyOf(ctx, blockKey);

  // bundle_pair: headline over an AI lifestyle image.
  return (
    <div style={{ backgroundColor: surfaces.light.bg }}>
      <div className="px-8 pt-10 pb-4 text-center" style={{ fontFamily: fonts.body }}>
        <PText
          ctx={ctx}
          blockKey={blockKey}
          field="headline"
          value={copy.headline}
          placeholder="Your headline goes here"
          className="text-[28px] font-extrabold leading-tight"
          style={{ color: colors.text, fontFamily: fonts.heading }}
          bold={{ color: colors.accent }}
          testId="editable-preset-hero-headline"
        />
      </div>
      <PresetAiImage ctx={ctx} slotId={blockKey} alt="Lifestyle" />
      <div className="px-8 pb-9 pt-2 text-center">
        <PresetCta ctx={ctx} blockKey={blockKey} label={copy.ctaLabel} className="mt-3" />
      </div>
    </div>
  );
}

function FactBandSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { fonts, surfaces, colors } = ctx.style;
  const d = surfaces.dark;
  const copy = presetCopyOf(ctx, blockKey);
  return (
    <div
      className="px-9 py-9 text-center"
      style={{ backgroundColor: d.bg, color: d.text, fontFamily: fonts.body }}
    >
      <div
        className="mx-auto mb-4 h-[2px] w-10 rounded-full"
        style={{ backgroundColor: colors.accent }}
      />
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="text"
        value={copy.text}
        placeholder="A bold fact goes here"
        multiline
        className="text-[19px] font-medium leading-relaxed"
        style={{ fontFamily: fonts.heading }}
        bold={{ color: colors.accent, fontWeight: 800 }}
        testId="editable-preset-fact-band"
      />
    </div>
  );
}

function BodySection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const l = surfaces.light;
  const copy = presetCopyOf(ctx, blockKey);
  const paragraphs =
    Array.isArray(copy.paragraphs) && copy.paragraphs.length
      ? copy.paragraphs
      : ctx.editor
        ? ["", "", ""]
        : [];
  return (
    <div
      className="px-9 py-10"
      style={{ backgroundColor: l.bg, color: l.text, fontFamily: fonts.body }}
    >
      {(copy.headline || ctx.editor) && (
        <PText
          ctx={ctx}
          blockKey={blockKey}
          field="headline"
          value={copy.headline}
          placeholder={ctx.editor ? "Optional intro headline" : ""}
          className="mb-4 text-center text-[22px] font-bold leading-snug"
          style={{ fontFamily: fonts.heading }}
          testId="editable-preset-body-headline"
        />
      )}
      <div className="space-y-4">
        {paragraphs.map((p, i) => (
          <PText
            key={i}
            ctx={ctx}
            blockKey={blockKey}
            value={p}
            placeholder="Paragraph copy goes here"
            onCommit={(v) => patchArrayItem(ctx, blockKey, "paragraphs", i, v)}
            multiline
            className="text-[15px] leading-relaxed"
            bold={{ fontWeight: 700 }}
            testId={`editable-preset-body-paragraph-${i}`}
          />
        ))}
      </div>
      {(copy.pullLine || ctx.editor) && (
        <PText
          ctx={ctx}
          blockKey={blockKey}
          field="pullLine"
          value={copy.pullLine}
          placeholder={ctx.editor ? "Optional italic pull line" : ""}
          className="mt-5 text-center text-[17px] italic"
          style={{ color: colors.accent, fontFamily: fonts.heading }}
          testId="editable-preset-pull-line"
        />
      )}
      {(copy.closingLine || ctx.editor) && (
        <PText
          ctx={ctx}
          blockKey={blockKey}
          field="closingLine"
          value={copy.closingLine}
          placeholder={ctx.editor ? "Optional closing line" : ""}
          className="mt-4 text-center text-[15px] font-bold"
          testId="editable-preset-closing-line"
        />
      )}
      {copy.ctaLabel && (
        <div className="text-center">
          <PresetCta
            ctx={ctx}
            blockKey={blockKey}
            label={copy.ctaLabel}
            fallback="Learn more"
          />
        </div>
      )}
    </div>
  );
}

function SectionHeaderBand({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const t = surfaces.tinted;
  const copy = presetCopyOf(ctx, blockKey);
  return (
    <div
      className="px-8 py-7 text-center"
      style={{ backgroundColor: t.bg, color: t.text, fontFamily: fonts.body }}
    >
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="headline"
        value={copy.headline}
        placeholder="A benefit line goes here"
        className="text-[20px] font-bold leading-snug"
        style={{ fontFamily: fonts.heading }}
        bold={{ color: colors.accent }}
        testId="editable-preset-section-header"
      />
    </div>
  );
}

function IngredientSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const d = surfaces.dark;
  const copy = presetCopyOf(ctx, blockKey);
  return (
    <div
      className="px-9 py-10 text-center"
      style={{ backgroundColor: d.bg, color: d.text, fontFamily: fonts.body }}
    >
      <div
        className="text-[10px] font-bold uppercase tracking-[0.22em]"
        style={{ color: colors.accent }}
      >
        Spotlight ingredient
      </div>
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="ingredientName"
        value={copy.ingredientName}
        placeholder="Ingredient name"
        className="mt-2 text-[26px] font-extrabold"
        style={{ fontFamily: fonts.heading }}
        testId="editable-preset-ingredient-name"
      />
      <PresetAiImage
        ctx={ctx}
        slotId={blockKey}
        alt="Ingredient macro"
        className="mx-auto mt-5 max-w-[420px] overflow-hidden rounded-md"
      />
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="explainer"
        value={copy.explainer}
        placeholder="Why this ingredient matters"
        multiline
        className="mx-auto mt-5 max-w-[440px] text-[14px] leading-relaxed"
        style={{ opacity: 0.92 }}
        bold={{ color: colors.accent, fontWeight: 700 }}
        testId="editable-preset-ingredient-explainer"
      />
      <PresetCta ctx={ctx} blockKey={blockKey} label={copy.ctaLabel} outlineOnDark />
    </div>
  );
}

function BenefitsSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const l = surfaces.light;
  const copy = presetCopyOf(ctx, blockKey);
  const ribbons =
    Array.isArray(copy.ribbons) && copy.ribbons.length
      ? copy.ribbons
      : ctx.editor
        ? ["", "", "", ""]
        : [];
  const product = productPhotoUrl(ctx);
  return (
    <div
      className="px-9 py-10 text-center"
      style={{ backgroundColor: l.bg, color: l.text, fontFamily: fonts.body }}
    >
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="headline"
        value={copy.headline}
        placeholder="Beyond the basics"
        className="text-[22px] font-extrabold uppercase tracking-wide"
        style={{ fontFamily: fonts.heading }}
        bold={{ color: colors.accent }}
        testId="editable-preset-benefits-headline"
      />
      {product && (
        <img
          src={product}
          alt="Product"
          className="mx-auto mt-5 max-h-44 w-auto object-contain"
          data-testid="img-preset-benefits-product"
        />
      )}
      <div className="mx-auto mt-6 max-w-[430px] space-y-2.5">
        {ribbons.map((r, i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-4 py-3 text-left"
            style={{
              backgroundColor: i % 2 === 0 ? surfaces.tinted.bg : colors.card,
              border: `1px solid ${l.border}`,
            }}
          >
            <BadgeCheck className="h-5 w-5 shrink-0" style={{ color: colors.accent }} />
            <PText
              ctx={ctx}
              blockKey={blockKey}
              value={r}
              placeholder="A short benefit"
              onCommit={(v) => patchArrayItem(ctx, blockKey, "ribbons", i, v)}
              as="span"
              className="text-[14px] font-semibold"
              testId={`editable-preset-ribbon-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function OfferLockupSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const a = surfaces.accent;
  const copy = presetCopyOf(ctx, blockKey);
  return (
    <div
      className="px-9 py-10 text-center"
      style={{ backgroundColor: a.bg, color: a.text, fontFamily: fonts.body }}
    >
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="leadLine"
        value={copy.leadLine}
        placeholder="Your offer lead line"
        className="text-[17px] font-semibold"
        style={{ fontFamily: fonts.heading }}
        testId="editable-preset-lockup-lead"
      />
      {(copy.code || ctx.editor) && (
        <div
          className="mx-auto mt-4 inline-block border-2 border-dashed px-7 py-3 text-[22px] font-extrabold tracking-[0.2em]"
          style={{ borderColor: a.text }}
        >
          <PText
            ctx={ctx}
            blockKey={blockKey}
            field="code"
            value={copy.code}
            placeholder="CODE"
            as="span"
            testId="editable-preset-lockup-code"
          />
        </div>
      )}
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="shippingLine"
        value={copy.shippingLine}
        placeholder={ctx.editor ? "Optional shipping line" : ""}
        className="mt-3 text-[13px]"
        style={{ opacity: 0.85 }}
        testId="editable-preset-lockup-shipping"
      />
      <div
        className="mx-auto mt-5 inline-block px-8 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em]"
        style={{
          backgroundColor: surfaces.dark.bg,
          color: surfaces.dark.text,
          borderRadius: ctx.style.button.borderRadius,
        }}
        data-testid={`preset-cta-${blockKey}`}
      >
        <PText
          ctx={ctx}
          blockKey={blockKey}
          field="ctaLabel"
          value={copy.ctaLabel}
          placeholder="Shop the sale"
          as="span"
          testId={`editable-preset-cta-${blockKey}`}
        />
      </div>
    </div>
  );
}

function CategoryGridSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const l = surfaces.light;
  const copy = presetCopyOf(ctx, blockKey);
  const categories = Array.isArray(copy.categories) ? copy.categories : [];
  if (!categories.length && !ctx.editor) return null;
  return (
    <div
      className="px-8 py-9"
      style={{ backgroundColor: l.bg, color: l.text, fontFamily: fonts.body }}
    >
      <div
        className="mb-5 text-center text-[13px] font-bold uppercase tracking-[0.18em]"
        style={{ color: l.muted }}
      >
        Shop by category
      </div>
      {!categories.length && ctx.editor && (
        <div
          data-export-exclude
          className="rounded border border-dashed px-4 py-6 text-center text-[13px]"
          style={{ borderColor: l.border, color: l.muted }}
        >
          No categories available — this brand has no product photos to build the grid from.
        </div>
      )}
      <div className="space-y-3">
        {categories.map((c, i) => {
          const img = exportSafe(ctx, c.imageUrl);
          const flip = i % 2 === 1;
          return (
            <div
              key={i}
              className={`flex items-stretch gap-0 overflow-hidden ${flip ? "flex-row-reverse" : ""}`}
              style={{ border: `1px solid ${l.border}` }}
              data-testid={`preset-category-row-${i}`}
            >
              <div className="w-1/2 min-h-[110px]" style={{ backgroundColor: surfaces.tinted.bg }}>
                {img && (
                  <img src={img} alt={c.label} className="h-full w-full object-cover" />
                )}
              </div>
              <div className="flex w-1/2 flex-col items-center justify-center px-4 py-6 text-center">
                <PText
                  ctx={ctx}
                  blockKey={blockKey}
                  value={c.label}
                  placeholder="Category"
                  onCommit={(v) => {
                    const next = categories.map((x, xi) =>
                      xi === i ? { ...x, label: v } : x,
                    );
                    patchPreset(ctx, blockKey, { categories: next });
                  }}
                  className="text-[17px] font-bold uppercase tracking-wide"
                  style={{ fontFamily: fonts.heading }}
                  testId={`editable-preset-category-label-${i}`}
                />
                <div
                  className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] underline underline-offset-4"
                  style={{ color: colors.accent }}
                >
                  Shop now
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChecklistSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const t = surfaces.tinted;
  const copy = presetCopyOf(ctx, blockKey);
  const items =
    Array.isArray(copy.items) && copy.items.length
      ? copy.items
      : ctx.editor
        ? ["", "", "", ""]
        : [];
  const product = productPhotoUrl(ctx);
  return (
    <div
      className="px-9 py-10"
      style={{ backgroundColor: t.bg, color: t.text, fontFamily: fonts.body }}
    >
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="headline"
        value={copy.headline}
        placeholder="Why it works"
        className="text-center text-[22px] font-extrabold uppercase tracking-wide"
        style={{ fontFamily: fonts.heading }}
        bold={{ color: colors.accent }}
        testId="editable-preset-checklist-headline"
      />
      <div className="mt-6 flex items-center gap-6">
        {product && (
          <img
            src={product}
            alt="Product"
            className="max-h-48 w-2/5 object-contain"
            data-testid="img-preset-checklist-product"
          />
        )}
        <div className={`space-y-3 ${product ? "w-3/5" : "w-full"}`}>
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: colors.accent }}
              >
                <Check className="h-3.5 w-3.5" style={{ color: colors.onAccent }} />
              </span>
              <PText
                ctx={ctx}
                blockKey={blockKey}
                value={it}
                placeholder="Checklist item"
                onCommit={(v) => patchArrayItem(ctx, blockKey, "items", i, v)}
                as="span"
                className="text-[14px] font-semibold"
                testId={`editable-preset-checklist-item-${i}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewsSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const l = surfaces.light;
  const copy = presetCopyOf(ctx, blockKey);
  const cards =
    Array.isArray(copy.reviewCards) && copy.reviewCards.length
      ? copy.reviewCards
      : ctx.editor
        ? [
            { quote: "", name: "", rating: 5 },
            { quote: "", name: "", rating: 5 },
          ]
        : [];
  if (!cards.length) return null;
  const product = productPhotoUrl(ctx);
  return (
    <div
      className="px-9 py-10"
      style={{ backgroundColor: l.bg, color: l.text, fontFamily: fonts.body }}
    >
      <div
        className="mb-5 text-center text-[13px] font-bold uppercase tracking-[0.18em]"
        style={{ color: l.muted }}
      >
        What customers say
      </div>
      <div className="space-y-4">
        {cards.slice(0, 2).map((card, i) => (
          <div
            key={i}
            className="px-6 py-6 text-center"
            style={{ backgroundColor: colors.card, border: `1px solid ${l.border}` }}
            data-testid={`preset-review-card-${i}`}
          >
            <Stars rating={card.rating} color={colors.accent} />
            <PText
              ctx={ctx}
              blockKey={blockKey}
              value={card.quote}
              placeholder="A real customer review goes here — add one from your saved reviews."
              onCommit={(v) => {
                const next = cards.map((x, xi) => (xi === i ? { ...x, quote: v } : x));
                patchPreset(ctx, blockKey, { reviewCards: next });
              }}
              multiline
              className="mt-3 text-[14px] italic leading-relaxed"
              testId={`editable-preset-review-quote-${i}`}
            />
            {product && i === 0 && (
              <img
                src={product}
                alt="Product"
                className="mx-auto mt-4 max-h-28 w-auto object-contain"
              />
            )}
            <PText
              ctx={ctx}
              blockKey={blockKey}
              value={card.name}
              placeholder="Reviewer name"
              onCommit={(v) => {
                const next = cards.map((x, xi) => (xi === i ? { ...x, name: v } : x));
                patchPreset(ctx, blockKey, { reviewCards: next });
              }}
              className="mt-3 text-[12px] font-bold uppercase tracking-[0.14em]"
              style={{ color: l.muted }}
              testId={`editable-preset-review-name-${i}`}
            />
          </div>
        ))}
      </div>
      {(copy.ctaLabel || ctx.editor) && (
        <div className="text-center">
          <PresetCta ctx={ctx} blockKey={blockKey} label={copy.ctaLabel} />
        </div>
      )}
    </div>
  );
}

function ComparisonBandSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const d = surfaces.dark;
  const copy = presetCopyOf(ctx, blockKey);
  const ribbons =
    Array.isArray(copy.ribbons) && copy.ribbons.length
      ? copy.ribbons
      : ctx.editor
        ? ["", "", "", ""]
        : [];
  const product = productPhotoUrl(ctx);
  return (
    <div
      className="px-9 py-10 text-center"
      style={{ backgroundColor: d.bg, color: d.text, fontFamily: fonts.body }}
    >
      <div className="flex items-center justify-center gap-4">
        <div className="flex items-center gap-1.5">
          <X className="h-4 w-4" style={{ color: d.muted }} />
          <PText
            ctx={ctx}
            blockKey={blockKey}
            field="villainLine"
            value={copy.villainLine}
            placeholder="The old way"
            as="span"
            className="text-[18px] font-semibold line-through"
            style={{ color: d.muted }}
            testId="editable-preset-villain-line"
          />
        </div>
        <span className="text-[12px] font-bold uppercase" style={{ color: d.muted }}>
          vs
        </span>
        <PText
          ctx={ctx}
          blockKey={blockKey}
          field="heroLine"
          value={copy.heroLine}
          placeholder="The better way"
          as="span"
          className="text-[20px] font-extrabold"
          style={{ color: colors.accent, fontFamily: fonts.heading }}
          bold={{ color: colors.accent }}
          testId="editable-preset-hero-line"
        />
      </div>
      {product && (
        <img
          src={product}
          alt="Product"
          className="mx-auto mt-5 max-h-44 w-auto object-contain"
          data-testid="img-preset-comparison-product"
        />
      )}
      <div className="mx-auto mt-6 flex max-w-[460px] flex-wrap justify-center gap-2">
        {ribbons.map((r, i) => (
          <span
            key={i}
            className="rounded-full border px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide"
            style={{ borderColor: d.border, color: d.text }}
          >
            <PText
              ctx={ctx}
              blockKey={blockKey}
              value={r}
              placeholder="Benefit"
              onCommit={(v) => patchArrayItem(ctx, blockKey, "ribbons", i, v)}
              as="span"
              testId={`editable-preset-comparison-ribbon-${i}`}
            />
          </span>
        ))}
      </div>
      <PresetCta ctx={ctx} blockKey={blockKey} label={copy.ctaLabel} fallback="Make the switch" outlineOnDark />
    </div>
  );
}

function StatBandSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const d = surfaces.dark;
  const copy = presetCopyOf(ctx, blockKey);
  return (
    <div
      className="px-9 py-9 text-center"
      style={{ backgroundColor: d.bg, color: d.text, fontFamily: fonts.body }}
    >
      <div className="mb-4 flex items-center justify-center gap-6">
        {STAT_ICONS.map((Icon, i) => (
          <Icon key={i} className="h-7 w-7" style={{ color: colors.accent }} />
        ))}
      </div>
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="text"
        value={copy.text}
        placeholder="The key stat goes here (verbatim from your supplied facts)"
        multiline
        className="mx-auto max-w-[440px] text-[16px] font-medium leading-relaxed"
        bold={{ color: colors.accent, fontWeight: 800 }}
        testId="editable-preset-stat-band"
      />
    </div>
  );
}

function TransitionSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { fonts, surfaces } = ctx.style;
  const t = surfaces.tinted;
  const copy = presetCopyOf(ctx, blockKey);
  return (
    <div
      className="px-10 py-8 text-center"
      style={{ backgroundColor: t.bg, color: t.text, fontFamily: fonts.heading }}
    >
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="text"
        value={copy.text}
        placeholder="A short transition line"
        className="text-[18px] italic leading-relaxed"
        testId="editable-preset-transition"
      />
    </div>
  );
}

function ProductRevealSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const l = surfaces.light;
  const copy = presetCopyOf(ctx, blockKey);
  const lines =
    Array.isArray(copy.revealLines) && copy.revealLines.length
      ? copy.revealLines
      : ctx.editor
        ? ["", ""]
        : [];
  const product = productPhotoUrl(ctx);
  return (
    <div
      className="px-9 py-10 text-center"
      style={{ backgroundColor: l.bg, color: l.text, fontFamily: fonts.body }}
    >
      {product && (
        <img
          src={product}
          alt="Product"
          className="mx-auto max-h-52 w-auto object-contain"
          data-testid="img-preset-reveal-product"
        />
      )}
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="headline"
        value={copy.headline}
        placeholder="Meet the product"
        className="mt-5 text-[24px] font-extrabold"
        style={{ fontFamily: fonts.heading }}
        testId="editable-preset-reveal-headline"
      />
      <div className="mx-auto mt-3 max-w-[430px] space-y-2">
        {lines.slice(0, 2).map((ln, i) => (
          <PText
            key={i}
            ctx={ctx}
            blockKey={blockKey}
            value={ln}
            placeholder="Supporting line"
            onCommit={(v) => patchArrayItem(ctx, blockKey, "revealLines", i, v)}
            className="text-[14px] leading-relaxed"
            bold={{ fontWeight: 700 }}
            testId={`editable-preset-reveal-line-${i}`}
          />
        ))}
      </div>
      <PresetCta ctx={ctx} blockKey={blockKey} label={copy.ctaLabel} fallback="Shop now" />
    </div>
  );
}

function ClosingSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts } = ctx.style;
  const copy = presetCopyOf(ctx, blockKey);
  const social: UniversalSocialLink[] = (
    (ctx.footer?.socialLinks || []) as UniversalSocialLink[]
  ).filter((s) => s?.platform);
  return (
    <div
      className="px-9 py-8 text-center"
      style={{ backgroundColor: colors.card, color: colors.text, fontFamily: fonts.body }}
    >
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="text"
        value={copy.text}
        placeholder="A short sign-off from the brand"
        multiline
        className="mx-auto max-w-[440px] text-[14px] leading-relaxed"
        testId="editable-preset-closing"
      />
      {social.length > 0 && (
        <div className="mt-4 flex items-center justify-center gap-4">
          {social.map((s, i) => (
            <a
              key={i}
              href={safeFooterHref(s.url)}
              onClick={ctx.editor ? (e) => e.preventDefault() : undefined}
              className="text-[11px] font-bold uppercase tracking-[0.14em] underline underline-offset-4"
              style={{ color: colors.accent }}
              data-testid={`link-preset-social-${i}`}
            >
              {s.platform}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function AboutSection({ ctx, blockKey }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const t = surfaces.tinted;
  const copy = presetCopyOf(ctx, blockKey);
  const logo = ctx.logoExportUrl || ctx.style.logoUrl;
  return (
    <div
      className="px-9 py-10 text-center"
      style={{ backgroundColor: t.bg, color: t.text, fontFamily: fonts.body }}
    >
      <div
        className="text-[10px] font-bold uppercase tracking-[0.22em]"
        style={{ color: colors.accent }}
      >
        Our story
      </div>
      {logo ? (
        <img src={logo} alt={ctx.brandName} className="mx-auto mt-3 h-8 w-auto object-contain" />
      ) : (
        <div
          className="mt-3 text-[15px] font-bold uppercase tracking-[0.18em]"
          style={{ fontFamily: fonts.heading }}
        >
          {ctx.brandName}
        </div>
      )}
      <PText
        ctx={ctx}
        blockKey={blockKey}
        field="aboutText"
        value={copy.aboutText}
        placeholder="What this brand stands for"
        multiline
        className="mx-auto mt-4 max-w-[440px] text-[14px] leading-relaxed"
        style={{ opacity: 0.92 }}
        testId="editable-preset-about"
      />
    </div>
  );
}

// The ONE standard footer (user decision, Aug 2026): identical to the standard
// FooterBlock / server standardFooterMjml — brand name, address, unsubscribe,
// brand colors. Trust badges / legal text no longer render "for now".
function TrustSection({ ctx }: { ctx: PreviewCtx; blockKey: string }) {
  const { colors, fonts, surfaces } = ctx.style;
  const f = ctx.footer;
  const unsubLabel = f?.unsubscribeLabel?.trim() || "Unsubscribe";
  const brandLine = f?.companyName?.trim() || ctx.brandName || "";
  return (
    <div
      className="text-center px-6 py-8"
      // Footer blends into the email: page background + contrast-safe text
      // (mirrors FooterBlock / server standardFooterMjml).
      style={{ backgroundColor: surfaces.page.bg, color: surfaces.page.text, fontFamily: fonts.body }}
    >
      <div
        className="mx-auto mb-4 h-[2px] w-10 rounded-full"
        style={{ backgroundColor: colors.accent }}
      />
      {brandLine && (
        <div className="font-bold tracking-[0.15em] uppercase text-[11px]" style={{ opacity: 0.95 }}>
          {brandLine}
        </div>
      )}
      {f?.address?.trim() && (
        <div className="mt-1.5 whitespace-pre-line text-[11px] leading-relaxed" style={{ opacity: 0.55 }}>
          {f.address}
        </div>
      )}
      <div className="mt-3.5 text-[11px]" style={{ opacity: 0.7 }}>
        <a
          href={safeFooterHref(f?.unsubscribeUrl)}
          onClick={ctx.editor ? (e) => e.preventDefault() : undefined}
          style={{ color: "inherit", textDecoration: "underline" }}
          data-testid="link-preset-unsubscribe"
        >
          {unsubLabel}
        </a>
      </div>
    </div>
  );
}

// ── Dispatch ──────────────────────────────────────────────────────────────────

// True when a block instance id belongs to the preset section family.
export function isEmailPresetSectionKey(key: string): boolean {
  return key.startsWith("preset_");
}

// The editor's label for a preset section (template-specific where defined).
export function emailPresetSectionLabel(
  templateKey: string | null | undefined,
  sectionKey: string,
): string {
  const def = getEmailPresetTemplate(templateKey);
  const s = def?.sections.find((x) => x.key === sectionKey);
  if (s) return s.label;
  return sectionKey.replace(/^preset_/, "").replace(/_/g, " ");
}

// Render one preset section. Returns null for unknown keys so the caller can
// fall through to the archetype renderer (never expected for preset emails).
export function renderEmailPresetBlock(key: string, ctx: PreviewCtx): ReactNode | null {
  switch (key) {
    case "preset_header":
      return <HeaderSection ctx={ctx} />;
    case "preset_hero":
      return <HeroSection ctx={ctx} blockKey={key} />;
    case "preset_fact_band":
      return <FactBandSection ctx={ctx} blockKey={key} />;
    case "preset_body":
      return <BodySection ctx={ctx} blockKey={key} />;
    case "preset_section_header":
      return <SectionHeaderBand ctx={ctx} blockKey={key} />;
    case "preset_ingredient":
      return <IngredientSection ctx={ctx} blockKey={key} />;
    case "preset_benefits":
      return <BenefitsSection ctx={ctx} blockKey={key} />;
    case "preset_offer_lockup":
      return <OfferLockupSection ctx={ctx} blockKey={key} />;
    case "preset_category_grid":
      return <CategoryGridSection ctx={ctx} blockKey={key} />;
    case "preset_checklist":
      return <ChecklistSection ctx={ctx} blockKey={key} />;
    case "preset_reviews":
      return <ReviewsSection ctx={ctx} blockKey={key} />;
    case "preset_comparison_band":
      return <ComparisonBandSection ctx={ctx} blockKey={key} />;
    case "preset_stat_band":
      return <StatBandSection ctx={ctx} blockKey={key} />;
    case "preset_transition":
      return <TransitionSection ctx={ctx} blockKey={key} />;
    case "preset_product_reveal":
      return <ProductRevealSection ctx={ctx} blockKey={key} />;
    case "preset_closing":
      return <ClosingSection ctx={ctx} blockKey={key} />;
    case "preset_about":
      return <AboutSection ctx={ctx} blockKey={key} />;
    case "preset_trust":
      return <TrustSection ctx={ctx} blockKey={key} />;
    default:
      return null;
  }
}
